package dev.lingvo.shared.screenshots.doc

import dev.lingvo.shared.StoreLimits
import kotlinx.serialization.SerializationException
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive

/** JSON Pointer (RFC 6901) paths, as [DesignIssue.path] carries them. */
internal object JsonPointer {
    fun child(parent: String, key: String): String = "$parent/" + key.replace("~", "~0").replace("/", "~1")

    fun child(parent: String, index: Int): String = "$parent/$index"

    /** Converts kotlinx's `$.boards[0].overrides['de']` into `/boards/0/overrides/de`; best effort. */
    fun fromKotlinxPath(path: String): String {
        if (!path.startsWith("$")) return ""
        val out = StringBuilder()
        var index = 1
        while (index < path.length) {
            when (path[index]) {
                '.' -> {
                    var end = index + 1
                    while (end < path.length && path[end] != '.' && path[end] != '[') end++
                    out.append(child("", path.substring(index + 1, end)))
                    index = end
                }
                '[' -> {
                    val close = path.indexOf(']', index)
                    if (close < 0) return out.toString()
                    val inner = path.substring(index + 1, close).trim('\'', '"')
                    out.append(child("", inner))
                    index = close + 1
                }
                else -> return out.toString()
            }
        }
        return out.toString()
    }
}

/**
 * The parsed-tree counterpart of [JsonPrecheck], run iteratively over a [JsonElement] before it is decoded.
 *
 * It re-applies the depth, value-count and string limits (an element can arrive already parsed, inside a request
 * envelope) and adds checks text scanning cannot make: no list longer than [DesignLimits.MAX_ARRAY_ITEMS]; every
 * non-string literal is a JSON number, boolean or null (the tree reader accepts any unquoted token, and the JVM's
 * `toDouble` would accept `1.5d`); every number is finite (`1e400` parses to Infinity and is NOT_FINITE with its
 * path) and either zero or at least [DesignLimits.MIN_MAGNITUDE] in magnitude.
 */
internal object JsonElementScan {
    private val JSON_NUMBER = Regex("^-?(?:0|[1-9][0-9]*)(?:\\.[0-9]+)?(?:[eE][+-]?[0-9]+)?$")
    private val NON_FINITE_WORDS = setOf("NaN", "-NaN", "Infinity", "-Infinity", "+Infinity", "inf", "-inf")

    private class Item(val element: JsonElement, val path: String, val depth: Int)

    fun scan(root: JsonElement): DesignIssue? {
        val stack = ArrayList<Item>()
        stack.add(Item(root, "", 1))
        var values = 0
        while (stack.isNotEmpty()) {
            val item = stack.removeAt(stack.size - 1)
            values++
            if (values > DesignLimits.MAX_VALUES) {
                return DesignIssue(DesignIssueCode.TOO_MANY_TOKENS, "", "The design holds more than ${DesignLimits.MAX_VALUES} values.")
            }
            when (val element = item.element) {
                is JsonObject -> {
                    if (item.depth > DesignLimits.MAX_DEPTH) return tooDeep(item.path)
                    for ((key, value) in element) {
                        if (StoreLimits.codePointLength(key) > DesignLimits.MAX_STRING_CHARS) return stringTooLong(item.path)
                        stack.add(Item(value, JsonPointer.child(item.path, key.take(64)), item.depth + 1))
                    }
                }
                is JsonArray -> {
                    if (item.depth > DesignLimits.MAX_DEPTH) return tooDeep(item.path)
                    if (element.size > DesignLimits.MAX_ARRAY_ITEMS) {
                        return DesignIssue(
                            DesignIssueCode.TOO_MANY_TOKENS,
                            item.path,
                            "A list holds more than ${DesignLimits.MAX_ARRAY_ITEMS} items.",
                        )
                    }
                    element.forEachIndexed { index, value ->
                        stack.add(Item(value, JsonPointer.child(item.path, index), item.depth + 1))
                    }
                }
                is JsonNull -> Unit
                is JsonPrimitive -> checkPrimitive(element, item.path)?.let { return it }
            }
        }
        return null
    }

    private fun checkPrimitive(primitive: JsonPrimitive, path: String): DesignIssue? {
        val content = primitive.content
        if (primitive.isString) {
            return if (StoreLimits.codePointLength(content) > DesignLimits.MAX_STRING_CHARS) stringTooLong(path) else null
        }
        if (content == "true" || content == "false" || content == "null") return null
        if (content in NON_FINITE_WORDS) {
            return DesignIssue(DesignIssueCode.NOT_FINITE, path, "Numbers must be finite; found ${content.take(12)}.")
        }
        if (!JSON_NUMBER.matches(content)) {
            return DesignIssue(DesignIssueCode.MALFORMED, path, "Not a JSON value: ${content.take(24)}.")
        }
        val value = content.toDoubleOrNull()
        if (value == null || !value.isFinite()) {
            return DesignIssue(DesignIssueCode.NOT_FINITE, path, "Numbers must be finite; ${content.take(24)} is not.")
        }
        if (value != 0.0 && kotlin.math.abs(value) < DesignLimits.MIN_MAGNITUDE) {
            // Subnormals are spelled differently by the JVM and JavaScript; nothing in a design is this small.
            return DesignIssue(DesignIssueCode.OUT_OF_RANGE, path, "Numbers this close to zero are not allowed; write 0.")
        }
        return null
    }

    private fun tooDeep(path: String): DesignIssue =
        DesignIssue(DesignIssueCode.TOO_DEEP, path, "The design nests deeper than ${DesignLimits.MAX_DEPTH} levels.")

    private fun stringTooLong(path: String): DesignIssue = DesignIssue(
        DesignIssueCode.STRING_TOO_LONG,
        path,
        "A string is longer than ${DesignLimits.MAX_STRING_CHARS} characters.",
    )
}

/**
 * Maps a kotlinx [SerializationException] to one issue by the class of its message, with the JSON path it names.
 * The exception text is never passed through: it can quote the input, and its hints are about kotlinx, not Lingvo.
 */
internal object DecodeErrors {
    private val PATH = Regex("at path:? (\\$\\S*)")
    private val UNKNOWN_KEY = Regex("unknown key '([^']*)'")
    private val UNKNOWN_SUBCLASS = Regex("(?:subclass|class discriminator value) '([^']*)'")
    private val MISSING_FIELD = Regex("Fields? \\[?'?([^'\\]]*)'?\\]? (?:is|are) required")
    private val UNKNOWN_ENUM = Regex("does not contain element with name '([^']*)'")

    fun map(e: SerializationException): DesignIssue {
        val message = e.message.orEmpty()
        val path = PATH.find(message)?.groupValues?.get(1)?.let(JsonPointer::fromKotlinxPath) ?: ""
        UNKNOWN_KEY.find(message)?.let {
            val key = it.groupValues[1].take(40)
            return DesignIssue(DesignIssueCode.UNKNOWN_FIELD, JsonPointer.child(path, key), "Unknown field '$key'.")
        }
        if (message.contains("polymorphic", ignoreCase = true) || message.contains("discriminator", ignoreCase = true)) {
            val name = UNKNOWN_SUBCLASS.find(message)?.groupValues?.get(1)?.take(40)
            val text = if (name == null) "A typed value has no \"type\"." else "Unknown type '$name'."
            return DesignIssue(DesignIssueCode.UNKNOWN_TYPE, path, text)
        }
        if (message.contains("special floating-point", ignoreCase = true)) {
            return DesignIssue(DesignIssueCode.NOT_FINITE, path, "Numbers must be finite.")
        }
        MISSING_FIELD.find(message)?.let {
            return DesignIssue(DesignIssueCode.MALFORMED, path, "Missing required field ${it.groupValues[1].take(80)}.")
        }
        UNKNOWN_ENUM.find(message)?.let {
            return DesignIssue(DesignIssueCode.MALFORMED, path, "Unknown value '${it.groupValues[1].take(40)}'.")
        }
        return DesignIssue(DesignIssueCode.MALFORMED, path, "The design does not match the version 1 format.")
    }
}
